import React from 'react';

/**
 * Reusable Survey Question Preview Component
 * Renders a question exactly as members will see it in the Member Portal
 * Used in both SurveyBuilder (Live Preview) and SurveyPreview page
 */
export default function SurveyQuestionPreview({
  questionText,
  questionDescription,
  isRequired,
  questionType,
  inputType,
  subQuestions = [],
  multipleSelectAllowed,
  isOrderedSelection,
  multipleSelectLimit,
  constraints = [],
  conditions = [],
  sectionHeading,
  columnHeadings = [],
  showTotal,
  showGrandTotal,
  grandTotalColumns = [],
  totalFieldLabel = "Total",
  totalFieldNote,
  displayAsRowLabel,
  displayAsHeading,
  displayAsInformational,
  displayAsHorizontal = true,
  referenceSources = [],
  referenceDisplayMode = "single",
  referenceRowLabel = "",
  referenceColumns = [],
  referenceSourceNames = [],
  referenceFormula = "",
  parseConstraint,
  parseCondition
}) {
  if (!questionText) return null;

  const formatReferenceFormula = (formula, sourceNames) => {
    if (!formula) return "";
    return formula
      .match(/\d+|[A-Za-z]+|<=|>=|[+\-*/=<>]/g)
      ?.map((token) => {
        if (/^\d+$/.test(token)) {
          const index = parseInt(token, 10);
          const isSingleDigitSource = token.length === 1 && index < sourceNames.length;
          if (isSingleDigitSource) {
            const match = sourceNames[index]?.match(/^Q\d+/);
            return match ? match[0] : token;
          }
        }
        if (/^[A-Za-z]$/.test(token)) {
          const index = token.toUpperCase().charCodeAt(0) - 65;
          if (index >= 0 && index < sourceNames.length) {
            const match = sourceNames[index]?.match(/^Q\d+/);
            return match ? match[0] : token;
          }
        }
        return token;
      })
      .join('') || formula;
  };

  return (
    <div style={{ 
      backgroundColor: '#f8f9fa', 
      borderRadius: '8px',
      padding: '1.5rem',
      border: '1px solid #ddd'
    }}>
      {/* Section Heading */}
      {sectionHeading && (
        <div style={{ 
          fontSize: 'var(--font-xl)', 
          fontFamily: "'Neutraface-Demi', Arial, sans-serif",
          backgroundColor: '#f16d09',
          color: '#ffffff',
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          borderRadius: '4px'
        }}>
          {sectionHeading}
        </div>
      )}
      
      {/* Question text - hidden when displayed as row label in table OR when horizontal single-column OR when horizontal inputList OR when referenceValue (has its own rendering) */}
      {!(questionType === "singleInput" && columnHeadings.length > 0 && displayAsRowLabel) && 
       !(questionType === "singleInput" && columnHeadings.length === 0 && displayAsHorizontal && !displayAsInformational) &&
       !(questionType === "inputList" && displayAsHorizontal) &&
       questionType !== "referenceValue" && (
        <div style={{ 
          marginBottom: '0.5rem',
          ...(displayAsHeading && {
            fontSize: 'var(--font-xl)', 
            fontFamily: "'Neutraface-Demi', Arial, sans-serif",
            backgroundColor: '#f16d09',
            color: '#ffffff',
            padding: '0.75rem 1rem',
            borderRadius: '4px'
          })
        }}>
          <span style={{ 
            fontSize: displayAsHeading ? 'var(--font-xl)' : 'var(--font-base)', 
            fontFamily: "'Neutraface-Demi', Arial, sans-serif",
            color: displayAsHeading ? '#ffffff' : 'inherit'
          }}>
            {questionText}
          </span>
          {isRequired && (
            <span style={{ 
              color: displayAsHeading ? '#ffffff' : '#dc3d17', 
              marginLeft: '0.25rem',
              fontSize: displayAsHeading ? 'var(--font-xl)' : 'var(--font-base)'
            }}>
              *
            </span>
          )}
        </div>
      )}
      
      {questionDescription && (
        <p style={{ fontSize: 'var(--font-sm)', color: '#666', marginBottom: '1rem' }}>
          {questionDescription}
        </p>
      )}

      {/* Show badges for constraints and conditions */}
      {(constraints.length > 0 || conditions.length > 0 || (questionType === "multipleChoice" && (isOrderedSelection || multipleSelectLimit)) || (questionType === "referenceValue" && referenceSources.length > 0)) && (
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {constraints.filter(c => c !== "required").map((constraint, index) => (
            <span 
              key={index}
              style={{
                backgroundColor: '#FCED21',
                color: '#2e1719',
                padding: '0.25rem 0.6rem',
                borderRadius: '12px',
                fontSize: 'var(--font-xs)',
                fontFamily: "'Neutraface-Demi', Arial, sans-serif"
              }}
            >
              {parseConstraint ? parseConstraint(constraint, columnHeadings, subQuestions.map(sq => sq.Label || sq.label || '')) : constraint}
            </span>
          ))}
          {conditions.length > 0 && (
            <span 
              style={{
                backgroundColor: '#FFC60B',
                color: '#2e1719',
                padding: '0.25rem 0.6rem',
                borderRadius: '12px',
                fontSize: 'var(--font-xs)',
                fontFamily: "'Neutraface-Demi', Arial, sans-serif"
              }}
            >
              Conditional
            </span>
          )}
          {questionType === "referenceValue" && referenceSources.length > 0 && (
            <span 
              style={{
                backgroundColor: '#17a2b8',
                color: 'white',
                padding: '0.25rem 0.6rem',
                borderRadius: '12px',
                fontSize: 'var(--font-xs)',
                fontFamily: "'Neutraface-Demi', Arial, sans-serif"
              }}
            >
              Calculated
            </span>
          )}
          {questionType === "multipleChoice" && multipleSelectLimit && (
            <span 
              style={{
                backgroundColor: '#17a2b8',
                color: 'white',
                padding: '0.25rem 0.6rem',
                borderRadius: '12px',
                fontSize: 'var(--font-xs)',
                fontFamily: "'Neutraface-Demi', Arial, sans-serif"
              }}
            >
              Max {multipleSelectLimit} selections
            </span>
          )}
          {questionType === "multipleChoice" && isOrderedSelection && (
            <span 
              style={{
                backgroundColor: '#6f42c1',
                color: 'white',
                padding: '0.25rem 0.6rem',
                borderRadius: '12px',
                fontSize: 'var(--font-xs)',
                fontFamily: "'Neutraface-Demi', Arial, sans-serif"
              }}
            >
              Ranked/Ordered
            </span>
          )}
        </div>
      )}

      {/* Show input preview based on type */}
      {questionType === "singleInput" && inputType && (
        <>
          {displayAsInformational ? (
            // Informational only - just show text, no input
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              fontStyle: 'italic',
              color: '#666'
            }}>
              Informational text only (no input field)
            </div>
          ) : columnHeadings.length > 0 ? (
            // Multi-column single input (single-row table)
            <div style={{
              backgroundColor: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              overflow: "hidden"
            }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    {displayAsRowLabel && (
                      <th style={{ padding: "0.75rem", textAlign: "left", width: "30%" }}>
                        {/* Empty cell for question label alignment */}
                      </th>
                    )}
                    {columnHeadings.map((heading, idx) => (
                      <th key={idx} style={{ padding: "0.75rem", fontWeight: "bold", textAlign: "center" }}>
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {displayAsRowLabel && (
                      <td style={{ padding: "0.5rem 0.75rem", fontWeight: "500", verticalAlign: "middle" }}>
                        {questionText.replace(/^\d+\.\d+\s+/, '')}
                      </td>
                    )}
                    {columnHeadings.map((col, colIdx) => (
                      <td key={colIdx} style={{ padding: "0.5rem", textAlign: "center" }}>
                        <input 
                          type={inputType}
                          className="form-control"
                          disabled
                          placeholder={inputType === "number" ? "0" : ""}
                          style={{
                            maxWidth: inputType === "number" ? "120px" : "100%",
                            textAlign: inputType === "number" ? "right" : "left",
                            fontFamily: inputType === "number" ? "monospace" : "inherit",
                            margin: "0 auto"
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          ) : displayAsHorizontal ? (
            // Horizontal layout - table row with question | input
            <div style={{
              backgroundColor: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              overflow: "hidden"
            }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ 
                      padding: "0.75rem",
                      color: "#2e1719",
                      width: "60%",
                      verticalAlign: "middle"
                    }}>
                      <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>{questionText}</span>
                      {isRequired && <span style={{ color: '#dc3d17', marginLeft: '0.25rem' }}>*</span>}
                    </td>
                    <td style={{ padding: "0.5rem", width: "40%" }}>
                      {inputType === "boolean" ? (
                        // Boolean: Show Yes/No radio buttons
                        <div>
                          <div className="form-check">
                            <input 
                              type="radio"
                              className="form-check-input"
                              disabled
                              id="preview-yes"
                            />
                            <label className="form-check-label" htmlFor="preview-yes">
                              Yes
                            </label>
                          </div>
                          <div className="form-check">
                            <input 
                              type="radio"
                              className="form-check-input"
                              disabled
                              id="preview-no"
                            />
                            <label className="form-check-label" htmlFor="preview-no">
                              No
                            </label>
                          </div>
                        </div>
                      ) : (
                        // Other input types: regular input
                        <input 
                          type={inputType}
                          className="form-control"
                          disabled
                          placeholder={inputType === "number" ? "0" : `Enter ${inputType}...`}
                          style={{ 
                            maxWidth: inputType === "number" ? "200px" : "100%",
                            textAlign: inputType === "number" ? "right" : "left",
                            fontFamily: inputType === "number" ? "monospace" : "inherit"
                          }}
                        />
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            // Vertical layout - standard input below question
            inputType === "boolean" ? (
              // Boolean: Show Yes/No radio buttons
              <div>
                <div className="form-check">
                  <input 
                    type="radio"
                    className="form-check-input"
                    disabled
                    id="preview-vert-yes"
                  />
                  <label className="form-check-label" htmlFor="preview-vert-yes">
                    Yes
                  </label>
                </div>
                <div className="form-check">
                  <input 
                    type="radio"
                    className="form-check-input"
                    disabled
                    id="preview-vert-no"
                  />
                  <label className="form-check-label" htmlFor="preview-vert-no">
                    No
                  </label>
                </div>
              </div>
            ) : (
              // Other input types: regular input
              <input 
                type={inputType}
                className="form-control"
                disabled
                placeholder={inputType === "number" ? "0" : `Enter ${inputType}...`}
                style={{ 
                  maxWidth: inputType === "number" ? "200px" : "400px",
                  textAlign: inputType === "number" ? "right" : "left",
                  fontFamily: inputType === "number" ? "monospace" : "inherit"
                }}
              />
            )
          )}
        </>
      )}

      {/* Reference Value - Display as label with yellow box */}
      {questionType === "referenceValue" && (
        <>
          {referenceDisplayMode === "tableRow" && referenceColumns.length > 0 ? (
            <>
              <table style={{
                width: "100%",
                borderCollapse: "collapse",
                backgroundColor: "white",
                border: "1px solid #dee2e6",
                marginBottom: "1rem"
              }}>
                <thead>
                  <tr>
                    <th style={{ width: "40%", padding: "0.75rem", borderBottom: "2px solid #dee2e6" }} />
                    {referenceColumns.map((column, index) => (
                      <th key={index} style={{
                        padding: "0.75rem",
                        textAlign: "center",
                        borderBottom: "2px solid #dee2e6",
                        fontFamily: "'Neutraface-Demi', Arial, sans-serif"
                      }}>
                        {column.Heading || `Column ${index + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{
                      padding: "0.75rem",
                      borderBottom: "1px solid #dee2e6",
                      fontFamily: "'Neutraface-Demi', Arial, sans-serif"
                    }}>
                      {referenceRowLabel || questionText}
                      {questionDescription && (
                        <div style={{ fontSize: 'var(--font-sm)', color: '#666', marginTop: '0.35rem', fontFamily: 'inherit' }}>
                          {questionDescription}
                        </div>
                      )}
                    </td>
                    {referenceColumns.map((column, index) => (
                      <td key={index} style={{
                        padding: "0.75rem",
                        textAlign: "center",
                        borderBottom: "1px solid #dee2e6"
                      }}>
                        <span style={{
                          backgroundColor: "#FFF3CD",
                          border: "2px solid #FFC60B",
                          borderRadius: "6px",
                          padding: ".35rem .75rem",
                          display: "inline-block",
                          fontFamily: "monospace"
                        }}>
                          (calculated)
                        </span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </>
          ) : (
          <>
          {/* Show question label */}
          <div style={{ 
            marginBottom: '0.5rem',
            ...(displayAsHeading && {
              fontSize: 'var(--font-xl)', 
              fontFamily: "'Neutraface-Demi', Arial, sans-serif",
              backgroundColor: '#f16d09',
              color: '#ffffff',
              padding: '0.75rem 1rem',
              borderRadius: '4px'
            })
          }}>
            <span style={{ 
              fontSize: displayAsHeading ? 'var(--font-xl)' : 'var(--font-base)', 
              fontFamily: "'Neutraface-Demi', Arial, sans-serif",
              color: displayAsHeading ? '#ffffff' : 'inherit'
            }}>
              {questionText}
            </span>
          </div>
          
          {questionDescription && (
            <p style={{ fontSize: 'var(--font-sm)', color: '#666', marginBottom: '1rem' }}>
              {questionDescription}
            </p>
          )}
          
          {/* Display calculated reference value in highlighted box */}
          <div style={{
            backgroundColor: "#FFF3CD",
            border: "3px solid #FFC60B",
            borderRadius: "6px",
            padding: ".5rem 1rem",
            display: "inline-block",
            marginLeft: "2rem"
          }}>
            <div style={{
              fontSize: "var(--font-subtitle)",
              color: "#492c28",
              fontFamily: "monospace",
              textAlign: "center",
              lineHeight: "1"
            }}>
              (calculated)
            </div>
          </div>
          
          {/* Source count and formula info below the box */}
          {referenceSources.length > 0 && (
            <div style={{
              fontSize: "var(--font-sm)",
              color: "#492c28",
              marginTop: "0.5rem"
            }}>
              <div style={{ fontStyle: "italic", marginBottom: "0.5rem" }}>
                Calculated from {referenceSources.length} source{referenceSources.length !== 1 ? 's' : ''}
                {referenceFormula && referenceFormula.length > 0 && (
                  <span> using formula: <code style={{ backgroundColor: '#e9ecef', padding: '0.125rem 0.25rem', borderRadius: '2px' }}>
                    {formatReferenceFormula(referenceFormula, referenceSourceNames)}
                  </code></span>
                )}
              </div>
              {referenceSourceNames.length > 0 && (
                <div style={{
                  backgroundColor: '#e9ecef',
                  borderLeft: '3px solid #17a2b8',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '4px'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Sources:</div>
                  {referenceSourceNames.map((name, idx) => (
                    <div key={idx} style={{ paddingLeft: '0.5rem' }}>
                      {name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </>
          )}
        </>
      )}

      {questionType === "multipleChoice" && subQuestions.length > 0 && (
        <div>
          {multipleSelectAllowed ? (
            // Checkboxes
            subQuestions.map(sq => (
              <div key={sq.id} className="form-check">
                <input 
                  type="checkbox" 
                  className="form-check-input" 
                  disabled 
                  id={`preview-${sq.id}`}
                  style={{ borderColor: '#495057', borderWidth: '2px' }}
                />
                <label 
                  htmlFor={`preview-${sq.id}`} 
                  style={{ 
                    color: '#2e1719', 
                    fontSize: 'var(--font-base)',
                    fontWeight: 'normal',
                    marginLeft: '0.25rem',
                    cursor: 'default',
                    opacity: 1
                  }}
                >
                  {sq.label || sq.Label}
                </label>
              </div>
            ))
          ) : (
            // Dropdown
            <select className="form-select" disabled style={{ maxWidth: '400px' }}>
              <option>-- Select an option --</option>
              {subQuestions.map(sq => (
                <option key={sq.id}>{sq.label || sq.Label}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {questionType === "inputList" && subQuestions.length > 0 && (
        <div style={{
          backgroundColor: "#fff",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
          overflow: "hidden"
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            {/* Show question as first row if horizontal display */}
            {displayAsHorizontal && (
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th colSpan={columnHeadings.length > 0 ? columnHeadings.length + 1 : 2} style={{ 
                    padding: "0.75rem",
                    textAlign: "left",
                    fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                    fontSize: 'var(--font-base)',
                    fontWeight: '600'
                  }}>
                    {questionText}
                    {isRequired && <span style={{ color: '#dc3d17', marginLeft: '0.25rem' }}>*</span>}
                  </th>
                </tr>
              </thead>
            )}
            {/* Column headings for multi-column mode */}
            {columnHeadings.length > 0 && (
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: "0.75rem", textAlign: "left" }}>
                    {/* Empty header for row labels column */}
                  </th>
                  {columnHeadings.map((heading, idx) => (
                    <th key={idx} style={{ padding: "0.75rem", fontWeight: "bold", textAlign: "center" }}>
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {subQuestions.map((sq, idx) => (
                <tr key={sq.id} style={{ borderBottom: idx < subQuestions.length - 1 ? "1px solid #dee2e6" : "none" }}>
                  <td style={{ 
                    padding: "0.75rem",
                    fontWeight: "500",
                    color: "#495057",
                    width: columnHeadings.length > 0 ? "auto" : "60%",
                    verticalAlign: "middle"
                  }}>
                    {sq.label || sq.Label}
                  </td>
                  {columnHeadings.length > 0 ? (
                    // Multi-column: render one input per column heading
                    columnHeadings.map((col, colIdx) => (
                      <td key={colIdx} style={{ padding: "0.5rem", textAlign: "center" }}>
                        {inputType === 'boolean' ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '0.875rem', color: '#666' }}>No</span>
                            <div className="form-check form-switch" style={{ display: 'inline-block', marginBottom: 0 }}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                disabled
                                style={{ cursor: 'not-allowed' }}
                              />
                            </div>
                            <span style={{ fontSize: '0.875rem', color: '#666' }}>Yes</span>
                          </div>
                        ) : (
                          <input 
                            type={inputType || "text"}
                            className="form-control"
                            disabled
                            placeholder={inputType === "number" ? "0" : ""}
                            style={{
                              maxWidth: inputType === "number" ? "120px" : "100%",
                              textAlign: inputType === "number" ? "right" : "left",
                              fontFamily: inputType === "number" ? "monospace" : "inherit",
                              margin: "0 auto"
                            }}
                          />
                        )}
                      </td>
                    ))
                  ) : (
                    // Single column: render one input
                    <td style={{ padding: "0.5rem", width: "40%" }}>
                      {inputType === 'boolean' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '0.875rem', color: '#666' }}>No</span>
                          <div className="form-check form-switch" style={{ display: 'inline-block', marginBottom: 0 }}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              disabled
                              style={{ cursor: 'not-allowed' }}
                            />
                          </div>
                          <span style={{ fontSize: '0.875rem', color: '#666' }}>Yes</span>
                        </div>
                      ) : (
                        <input 
                          type={inputType || "text"}
                          className="form-control"
                          disabled
                          placeholder={inputType === "number" ? "0" : "Enter text"}
                          style={{
                            maxWidth: inputType === "number" ? "150px" : "100%",
                            textAlign: inputType === "number" ? "right" : "left",
                            fontFamily: inputType === "number" ? "monospace" : "inherit"
                          }}
                        />
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {/* Totals row - inside tbody */}
              {showTotal && inputType === "number" && (
                <tr style={{ backgroundColor: '#fffacd', borderTop: '2px solid #f16d09' }}>
                  <td style={{ 
                    padding: "0.75rem",
                    fontWeight: "bold",
                    color: "#2e1719",
                    width: columnHeadings.length > 0 ? "40%" : "60%",
                    verticalAlign: "middle"
                  }}>
                    {totalFieldLabel || "Total"}
                    {totalFieldNote && (
                      <span style={{ 
                        fontSize: 'var(--font-xs)', 
                        color: '#666', 
                        fontWeight: 'normal',
                        marginLeft: '0.5rem'
                      }}>
                        ({totalFieldNote})
                      </span>
                    )}
                  </td>
                  {columnHeadings.length > 0 ? (
                    // Multi-column: show total for each column
                    columnHeadings.map((col, colIdx) => (
                      <td key={colIdx} style={{ padding: "0.75rem", textAlign: "center" }}>
                        <div style={{
                          textAlign: 'right',
                          fontFamily: 'monospace',
                          fontSize: 'var(--font-lg)',
                          fontWeight: 'bold',
                          color: '#2e1719',
                          maxWidth: '120px',
                          margin: '0 auto',
                          paddingRight: '1.5rem'
                        }}>
                          0
                        </div>
                      </td>
                    ))
                  ) : (
                    // Single column: show one total
                    <td style={{ padding: "0.75rem" }}>
                      <div style={{
                        textAlign: 'right',
                        fontFamily: 'monospace',
                        fontSize: 'var(--font-lg)',
                        fontWeight: 'bold',
                        color: '#2e1719',
                        maxWidth: '150px'
                      }}>
                        0
                      </div>
                    </td>
                  )}
                </tr>
              )}
              {/* Custom Total row - inside tbody (sum of selected columns only) */}
              {showGrandTotal && showTotal && inputType === "number" && columnHeadings.length > 0 && (
                <tr style={{ backgroundColor: '#ffd700', borderTop: '2px solid #f16d09' }}>
                  <td style={{ 
                    padding: "0.75rem",
                    fontWeight: "bold",
                    color: "#2e1719",
                    verticalAlign: "middle"
                  }}>
                    Custom Total
                    {grandTotalColumns.length > 0 && grandTotalColumns.length < columnHeadings.length && (
                      <span style={{ 
                        fontSize: 'var(--font-xs)', 
                        color: '#666', 
                        fontWeight: 'normal',
                        marginLeft: '0.5rem'
                      }}>
                        ({grandTotalColumns.map(idx => columnHeadings[idx]).join(' + ')})
                      </span>
                    )}
                  </td>
                  <td colSpan={columnHeadings.length} style={{ padding: "0.75rem 2rem 0.75rem 0.75rem", textAlign: "right" }}>
                    <div style={{
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      fontSize: 'var(--font-lg)',
                      fontWeight: 'bold',
                      color: '#2e1719',
                      maxWidth: '120px',
                      margin: '0 0 0 auto'
                    }}>
                      0
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
